import {extractIndoorMapLink} from "./util";

function newestFirst(history = []) {
    return [...(history || [])].sort((a, b) => Date.parse(b.created) - Date.parse(a.created));
}

function rankedLocations(payload) {
    return [...(payload?.rankedLocations || [])].sort((a, b) => b.confidence - a.confidence);
}

export function getAddressFields(eml = []) {
    const addresses = newestFirst(eml)
        .flatMap(update => (update.payload || []).flatMap(payload =>
            rankedLocations(payload)
                .map(location => {
                    const address = {...location.dispatchableAddress};
                    for (const line of (location.humanReadableLocation || '').split(/\r?\n/)) {
                        const match = line.trim().match(/^(City|State or Province|State|Province|Country):\s*(.*)$/i);
                        if (!match) continue;
                        const field = match[1].toLowerCase() === 'city' ? 'borough'
                            : match[1].toLowerCase() === 'country' ? 'country' : 'stateOrProvince';
                        if (!address[field]) address[field] = match[2].trim();
                    }
                    return address;
                })));
    const fields = ['borough', 'stateOrProvince', 'country'].map(field =>
        addresses.find(address => address[field])?.[field]);
    return fields;
}

export function resolveFloorPlan(history = [], topEmlLocation) {
        const updates = newestFirst(history);
        const payloads = [topEmlLocation, ...updates.flatMap(update => update.payload || [])];
        for (const payload of payloads) {
            const locations = rankedLocations(payload);
            for (const location of locations) {
                if (isSbmShipLocation(location)) return {isSbmShipLocation: true};
                const {parsedData} = extractIndoorMapLink(location.humanReadableLocation || '');
                const floorPlan = getFloorPlanLocation(parsedData);
                if (Object.values(floorPlan).some(Boolean)) return floorPlan;
            }
        }
        return {isEliOfficeLocation: false, isTestLocation: false, isMYLocation: false};
}

export function isSbmShipLocation(location) {
    const address = location?.dispatchableAddress;
    const text = typeof address === 'string' ? address : Object.values(address || {}).join(' ');
    return /sbm ship/i.test(text) || /sbm ship/i.test(location?.humanReadableLocation || '');
}

export function resolveLocationForDisplay(history = [], startIndex = 0, alsEnabled = true) {
    for (let i = startIndex; i < history.length; i += 1) {
        const payload = history[i]?.payload?.[0];
        const marker = getMarkerToDisplay(payload, alsEnabled);
        if (marker) {
            return {payload, marker};
        }
    }

    return null;
}

export function getMarkerToDisplay(locationPayload, alsEnabled = false) {
    const shipLocation = locationPayload?.rankedLocations?.find(isSbmShipLocation);
    if (shipLocation) {
        const x = shipLocation.tagGpsLongitude;
        const y = shipLocation.tagGpsLatitude;
        if (!hasCoordinate(x) || !hasCoordinate(y) || !Number.isFinite(Number(x)) || !Number.isFinite(Number(y))) return null;
        return {x: Number(x), y: Number(y), type: 'aps', id: '1', floor: shipLocation.dispatchableAddress?.floor};
    }
    const rankedLocation = locationPayload?.rankedLocations?.[0];
    const dispatchableAddress = rankedLocation?.dispatchableAddress;

    const {parsedData} = extractIndoorMapLink(rankedLocation?.humanReadableLocation || "");
    const floorPlanLocation = getFloorPlanLocation(parsedData);
    if (!Object.values(floorPlanLocation).some(Boolean)) {
        const apLocation = locationPayload?.rankedLocations?.find(location =>
            hasCoordinate(location.tagGpsLatitude) && hasCoordinate(location.tagGpsLongitude));
        if (apLocation) return {
            lat: apLocation.tagGpsLatitude, lng: apLocation.tagGpsLongitude,
            type: "aps", id: "1", floor: apLocation.dispatchableAddress?.floor,
        };
        if (hasCoordinate(locationPayload?.gpsLatitude) && hasCoordinate(locationPayload?.gpsLongitude)) return {
            lat: locationPayload.gpsLatitude, lng: locationPayload.gpsLongitude, type: "gps", id: "1",
        };
        return null;
    }

    if (!hasDispatchableAddress(dispatchableAddress)) {
        return null;
    }

    const useEstimatedGps = alsEnabled && locationPayload?.gpsEstimated;
    const lat = useEstimatedGps ? locationPayload?.gpsLatitude : rankedLocation?.tagGpsLatitude;
    const lng = useEstimatedGps ? locationPayload?.gpsLongitude : rankedLocation?.tagGpsLongitude;
    if (!hasCoordinate(lat) || !hasCoordinate(lng)) {
        return null;
    }

    return {
        lat,
        lng,
        type: "aps",
        id: "1",
        floor: dispatchableAddress.floor,
    };
}

export function resolveAllMarkersForDisplay(history = [], alsEnabled = true) {
    return history.reduce((markers, historyItem, index) => {
        const payload = historyItem?.payload?.[0];
        const marker = getMarkerToDisplay(payload, alsEnabled);

        if (!marker) {
            return markers;
        }

        markers.push({
            ...marker,
            id: `history-${index}`,
            isLatest: index === 0,
            timestampLabel: String(index),
        });

        return markers;
    }, []);
}

function hasDispatchableAddress(dispatchableAddress) {
    return !!dispatchableAddress && Object.keys(dispatchableAddress).length > 0;
}

function hasCoordinate(value) {
    return value !== null && value !== undefined && value !== "";
}

function getFloorPlanLocation(parsedData) {
    const locationText = [parsedData.room, parsedData.floor, ...parsedData.otherLines]
        .filter(Boolean).join(" ").toLowerCase();
    return {
        isEliOfficeLocation: locationText.includes("bedford") && locationText.includes("380"),
        isTestLocation: locationText.includes("myles brand hall"),
        isMYLocation: locationText.includes("manu tests") || locationText.includes("ble iot test"),
    };
}
